import { useCallback, useState } from 'react';
import type { Country, CountryDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ConfirmModal, ErrorState, Modal, RefreshButton, StatusBadge, TableSkeleton, TextField } from '../components/ui';

const NEW_DRAFT: CountryDraft = { name: '', code: '', digitLen: 10, example: '', providerFeePercent: 0 };

export function CountriesPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vCountries = useRealtimeVersion('countries');

  const isMobile = useIsMobile();
  const countries = useAsync(() => container.settings.listCountries.execute(), [container, vCountries]);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<CountryDraft>(NEW_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openEditor = useCallback((country: Country | null) => {
    if (country === null) {
      setEditorId(null);
      setDraft(NEW_DRAFT);
      return;
    }
    setEditorId(country.id);
    setDraft({ name: country.name, code: country.code, digitLen: country.digitLen, example: country.example, providerFeePercent: country.providerFeePercent });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.settings.saveCountry.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Pays ajouté' : 'Pays mis à jour');
      setEditorId(undefined);
      countries.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, countries]);

  const toggle = useCallback(
    async (id: string) => {
      try {
        await container.settings.toggleCountry.execute(id);
        await countries.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, countries],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await container.settings.deleteCountry.execute(id);
        toast.notify('Pays supprimé');
        countries.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, countries],
  );

  const rows = countries.data ?? [];

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">
            Pays proposés à l'inscription dans l'application et sur le site
          </div>
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('countries', 'create')}
            title={can('countries', 'create') ? '' : 'Réservé au super-admin'}
            onClick={() => openEditor(null)}
          >
            + Ajouter un pays
          </button>
          <RefreshButton onRefresh={countries.reload} loading={countries.loading} />
        </div>
      </div>

      {countries.loading ? <TableSkeleton rows={5} /> : null}
      {countries.error ? (
        <ErrorState
          status={countries.error.status}
          message={countries.error.message}
          onRetry={countries.reload}
        />
      ) : null}

      {rows.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {rows.map((country) => (
              <div key={country.id} className="bbm-mobile-card">
                <div className="bbm-mobile-card__head">
                  <div>
                    <div className="bbm-mobile-card__title">{country.name}</div>
                    <div className="bbm-mobile-card__sub">+{country.code}</div>
                  </div>
                  <StatusBadge status={country.isActive ? 'approved' : 'inactive'} label={country.isActive ? 'Actif' : 'Inactif'} />
                </div>
                <div className="bbm-mobile-card__fields">
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Code</span>
                    <span>{country.code}</span>
                  </div>
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Frais paiement</span>
                    <span>{country.providerFeePercent} %</span>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  <button type="button" className="bbm-btn" disabled={!can('countries', 'edit')} onClick={() => setPending({
                    title: 'Modifier la disponibilité ?',
                    body: 'La disponibilité de ce pays sera modifiée.',
                    fn: () => void toggle(country.id),
                  })}>{country.isActive ? 'Désactiver' : 'Activer'}</button>
                  <button type="button" className="bbm-btn" disabled={!can('countries', 'edit')} onClick={() => openEditor(country)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('countries', 'delete')} title={can('countries', 'delete') ? '' : 'Réservé au super-admin'} onClick={() => setPending({
                    title: 'Supprimer ce pays ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(country.id),
                  })}>Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 220 }}>Pays</div>
              <div style={{ width: 100 }}>Code</div>
              <div style={{ width: 120 }}>Indicatif</div>
              <div style={{ width: 120 }}>Frais paiement</div>
              <div style={{ width: 100 }}>Statut</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {rows.map((country) => (
              <div key={country.id} className="bbm-row">
                <div className="bbm-cell-strong" style={{ width: 220 }}>
                  {country.name}
                </div>
                <div className="bbm-cell" style={{ width: 100 }}>
                  {country.code}
                </div>
                <div className="bbm-cell" style={{ width: 120 }}>
                  {country.digitLen} chiffres
                </div>
                <div className="bbm-cell" style={{ width: 120 }}>
                  {country.providerFeePercent} %
                </div>
                <div style={{ width: 100 }}>
                  <StatusBadge
                    status={country.isActive ? 'approved' : 'inactive'}
                    label={country.isActive ? 'Actif' : 'Inactif'}
                  />
                </div>
                <div className="bbm-row__actions">
                  <button
                    type="button"
                    className="bbm-btn"
                    disabled={!can('countries', 'edit')}
                    onClick={() => setPending({
                      title: 'Modifier la disponibilité ?',
                      body: 'La disponibilité de ce pays sera modifiée.',
                      fn: () => void toggle(country.id),
                    })}
                  >
                    {country.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                  <button
                    type="button"
                    className="bbm-btn"
                    disabled={!can('countries', 'edit')}
                    onClick={() => openEditor(country)}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="bbm-btn bbm-btn--danger"
                    disabled={!can('countries', 'delete')}
                    title={can('countries', 'delete') ? '' : 'Réservé au super-admin'}
                    onClick={() => setPending({
                      title: 'Supprimer ce pays ?',
                      body: 'Cette action est irréversible.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void remove(country.id),
                    })}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouveau pays' : 'Modifier le pays'}
          width={480}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField
            label="Nom du pays"
            value={draft.name}
            onChange={(value) => setDraft({ ...draft, name: value })}
          />
          <TextField
            label="Indicatif téléphonique (ex: +225)"
            value={draft.code}
            maxLength={5}
            onChange={(value) => setDraft({ ...draft, code: value })}
          />
          <TextField
            label="Nb. chiffres (ex: 10)"
            type="number"
            value={draft.digitLen}
            onChange={(value) => setDraft({ ...draft, digitLen: Number(value) || 10 })}
          />
          <TextField
            label="Exemple (ex: 07 00 00 00 00)"
            value={draft.example}
            onChange={(value) => setDraft({ ...draft, example: value })}
          />
          <TextField
            label="Frais fournisseur paiement (%)"
            type="number"
            value={draft.providerFeePercent}
            onChange={(value) => setDraft({ ...draft, providerFeePercent: Number(value) || 0 })}
          />
        </Modal>
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
